// File: src/utils/driverMatching.js
// Matching an imported name against the drivers already racing a class.
//
// Two importers need this - MyRCM and LiveRC - and both timing systems spell
// names their own way, so the heuristics matter and must not be duplicated.
// Lifted out of MyRCMImport.js unchanged when the LiveRC importer arrived.

// Fuzzy name matching helper.
//
// Name comparisons run before the car number, deliberately. The number used to
// be the actively dangerous signal: timing systems assign numbers per class,
// but a driver's number was only unique per SERIES, so #1 in R8 and #1 in R8FE
// were different people colliding in one driver table, and matching on the
// number first produced a confident-looking match to the wrong person.
//
// The list handed in is one class's roster now, which is the same scope the
// timing system numbers in, so that trap is gone. The order stays anyway - a
// name is still the stronger signal, and a number is still what catches a
// driver spelled differently in the timing system than in the database. A match
// found that way is reported so the preview can ask for a check.
//
// Returns null, or { driver, matchedBy } where matchedBy is one of
// 'name' | 'reversedName' | 'partialName' | 'carNumber'.
export const fuzzyMatchDriver = (importedName, carNumber, existingDrivers) => {
  const cleanName = (name) => name.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, ' ').trim();
  const cleanImported = cleanName(importedName);

  // Try exact match first
  let match = existingDrivers.find(d => cleanName(d.name) === cleanImported);
  if (match) {
    return { driver: match, matchedBy: 'name' };
  }

  // Try reversing name order (Firstname Lastname vs Lastname Firstname)
  const parts = cleanImported.split(' ');
  if (parts.length >= 2) {
    // IMPORTANT: Create a NEW array, don't modify the original!
    const reversedParts = [...parts].reverse();
    const reversed = reversedParts.join(' ');
    match = existingDrivers.find(d => cleanName(d.name) === reversed);
    if (match) {
      return { driver: match, matchedBy: 'reversedName' };
    }

    // Try without middle initials/qualifiers
    const withoutMiddle = [parts[0], parts[parts.length - 1]].join(' ');
    match = existingDrivers.find(d => {
      const existingParts = cleanName(d.name).split(' ');
      if (existingParts.length < 2) return false;
      const existingSimple = [existingParts[0], existingParts[existingParts.length - 1]].join(' ');
      return existingSimple === withoutMiddle;
    });
    if (match) {
      return { driver: match, matchedBy: 'partialName' };
    }
  }

  // Last resort: the car number, when the DB has one recorded. No name
  // component agreed above, so this is the case most likely to be a
  // cross-class collision - flag it rather than trusting it.
  if (carNumber && !isNaN(carNumber)) {
    const carMatch = existingDrivers.find(d => d.car_number === carNumber);
    if (carMatch) {
      return { driver: carMatch, matchedBy: 'carNumber' };
    }
  }

  return null;
};

// A match found only by car number agreed on no part of the name, so it is the
// one most likely to be a cross-class collision. Both importers surface it.
export const matchNeedsReview = (match) => Boolean(match) && match.matchedBy === 'carNumber';

export default fuzzyMatchDriver;
